import { useState, useEffect, useRef, useCallback } from 'react';
import { Mic, MicOff, Volume2, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';

export default function MicInputSelector({ onDeviceSelected, onReady }) {
  const [devices, setDevices] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [permissionState, setPermissionState] = useState('unknown'); // unknown, requesting, granted, denied
  const [testing, setTesting] = useState(false);
  const [testLevel, setTestLevel] = useState(0);
  const [error, setError] = useState('');
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const animRef = useRef(null);

  const enumerateDevices = useCallback(async () => {
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devs.filter(d => d.kind === 'audioinput' && d.deviceId);
      const labeled = audioInputs.map(d => ({
        id: d.deviceId,
        label: d.label || (d.deviceId === 'default' ? 'Default Device' : `Microphone ${d.deviceId.slice(0, 6)}`),
        isDefault: d.deviceId === 'default',
      }));
      // Friendly labels
      labeled.forEach(d => {
        const ll = d.label.toLowerCase();
        if (ll.includes('airpod') || ll.includes('bluetooth')) d.type = 'bluetooth';
        else if (ll.includes('usb') || ll.includes('blue yeti') || ll.includes('rode')) d.type = 'external';
        else if (ll.includes('built-in') || ll.includes('internal') || d.isDefault) d.type = 'builtin';
        else d.type = 'other';
      });
      setDevices(labeled);
      if (labeled.length > 0 && !selectedId) {
        const def = labeled.find(d => d.isDefault) || labeled[0];
        setSelectedId(def.id);
      }
    } catch (e) {
      setError('Could not list audio devices');
    }
  }, [selectedId]);

  const requestPermission = async () => {
    setPermissionState('requesting');
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      setPermissionState('granted');
      await enumerateDevices();
    } catch (e) {
      if (e.name === 'NotAllowedError') {
        setPermissionState('denied');
        setError('Microphone access denied. Update browser settings.');
      } else {
        setPermissionState('denied');
        setError(e.message || 'Could not access microphone');
      }
    }
  };

  useEffect(() => {
    // Check if we already have permission
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'microphone' }).then(p => {
        if (p.state === 'granted') {
          setPermissionState('granted');
          enumerateDevices();
        } else if (p.state === 'denied') {
          setPermissionState('denied');
        }
      }).catch(() => {});
    }
    // Listen for device changes
    const handler = () => enumerateDevices();
    navigator.mediaDevices?.addEventListener('devicechange', handler);
    return () => {
      navigator.mediaDevices?.removeEventListener('devicechange', handler);
      stopTest();
    };
  }, []);

  const startTest = async () => {
    setTesting(true);
    try {
      const constraints = selectedId ? { audio: { deviceId: { exact: selectedId } } } : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      analyserRef.current = { ctx, analyser };

      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        setTestLevel(Math.min(100, avg * 1.5));
        animRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      setError('Could not access selected mic');
      setTesting(false);
    }
  };

  const stopTest = () => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (analyserRef.current?.ctx) analyserRef.current.ctx.close().catch(() => {});
    analyserRef.current = null;
    streamRef.current = null;
    setTesting(false);
    setTestLevel(0);
  };

  const confirmSelection = () => {
    if (onDeviceSelected) onDeviceSelected(selectedId);
    if (onReady) onReady(selectedId);
  };

  const selectedDevice = devices.find(d => d.id === selectedId);

  if (permissionState !== 'granted') {
    return (
      <Card className="bg-black/40 border-white/5 p-5 max-w-sm mx-auto text-center" data-testid="mic-permission-gate">
        <Mic className="w-10 h-10 text-violet-400 mx-auto mb-3" />
        <h3 className="font-bold mb-1">Microphone Access</h3>
        <p className="text-xs text-muted-foreground mb-4">We need mic access to record your battle.</p>
        {error && <p className="text-xs text-red-400 mb-3">{error}</p>}
        <Button onClick={requestPermission} disabled={permissionState === 'requesting'}
          className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="mic-request-btn">
          {permissionState === 'requesting' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mic className="w-4 h-4 mr-2" />}
          Enable Microphone
        </Button>
      </Card>
    );
  }

  return (
    <Card className="bg-black/40 border-white/5 p-4" data-testid="mic-input-selector">
      <h3 className="text-sm font-bold text-white/70 mb-3 flex items-center gap-2">
        <Mic className="w-4 h-4 text-violet-400" /> Recording Input
      </h3>

      {/* Device List */}
      <div className="space-y-1.5 mb-3" data-testid="mic-device-list">
        {devices.length === 0 ? (
          <p className="text-xs text-muted-foreground">No microphones detected</p>
        ) : (
          devices.map(d => (
            <button key={d.id} onClick={() => { setSelectedId(d.id); if (testing) { stopTest(); } }}
              className={`w-full flex items-center gap-2.5 p-2.5 rounded-lg text-left text-xs transition-all ${
                selectedId === d.id
                  ? 'bg-violet-500/15 border border-violet-500/30'
                  : 'bg-white/5 border border-white/5 hover:bg-white/10'
              }`} data-testid={`mic-device-${d.id.slice(0, 8)}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                d.type === 'bluetooth' ? 'bg-blue-500/20' : d.type === 'external' ? 'bg-green-500/20' : 'bg-white/10'
              }`}>
                {d.type === 'bluetooth' ? '🎧' : d.type === 'external' ? '🎙' : '📱'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{d.label}</p>
                <p className="text-[10px] text-white/40">
                  {d.type === 'bluetooth' ? 'Bluetooth / Headset' : d.type === 'external' ? 'External Mic' : d.isDefault ? 'Default Device' : 'Microphone'}
                </p>
              </div>
              {selectedId === d.id && <CheckCircle className="w-4 h-4 text-violet-400 shrink-0" />}
            </button>
          ))
        )}
      </div>

      {/* Selected indicator */}
      {selectedDevice && (
        <div className="text-xs text-white/50 mb-3 flex items-center gap-1.5 bg-white/5 px-3 py-1.5 rounded-lg" data-testid="mic-selected-label">
          <Volume2 className="w-3 h-3" />
          Recording from: <span className="text-white font-semibold">{selectedDevice.label}</span>
        </div>
      )}

      {/* Test Mic */}
      <div className="flex items-center gap-2 mb-3">
        <Button onClick={testing ? stopTest : startTest} variant="outline" size="sm" className="border-white/10" data-testid="mic-test-btn">
          {testing ? <MicOff className="w-3.5 h-3.5 mr-1 text-red-400" /> : <Mic className="w-3.5 h-3.5 mr-1" />}
          {testing ? 'Stop Test' : 'Test Mic'}
        </Button>
        {testing && (
          <div className="flex-1 h-3 bg-white/5 rounded-full overflow-hidden" data-testid="mic-level-meter">
            <div className="h-full rounded-full transition-all duration-75 bg-gradient-to-r from-green-500 via-yellow-500 to-red-500"
              style={{ width: `${testLevel}%` }} />
          </div>
        )}
      </div>

      {error && <p className="text-xs text-red-400 mb-2">{error}</p>}

      <Button onClick={confirmSelection} disabled={!selectedId || devices.length === 0}
        className="w-full bg-violet-600 hover:bg-violet-700 text-white" data-testid="mic-confirm-btn">
        <CheckCircle className="w-4 h-4 mr-2" /> Confirm & Ready
      </Button>
    </Card>
  );
}
